import Hls from "hls.js";
import screenfull from "screenfull";
import type { CodecSupport } from "../types/constants";
import cn from "@/utils/mergeClassNames";
import { isMobileLayout } from "@/utils/helper/isMobileLayout";

declare global {
  interface Document {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
  }

  interface HTMLElement {
    msRequestFullscreen?: () => Promise<void>;
    mozRequestFullscreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
    webkitEnterFullScreen?: () => Promise<void>;
    webkitEnterFullscreen?: () => Promise<void>;
  }

  interface ScreenOrientation {
    lock(orientation: string): Promise<void>;
  }
}

export function makeLandscape() {
  // this works on android, not iOS
  if (screen.orientation && screen.orientation.lock) {
    screen.orientation.lock("landscape").catch(() => {
      console.log("screen.orientation.lock() not support this device.");
    });
  }
}

export function supportFullscreen() {
  return screenfull.isEnabled;
}

export async function setFullscreen(element: HTMLElement) {
  if (screenfull.isEnabled) {
    await screenfull.request(element);
  }

  return screenfull.isEnabled;
}

export const setFakeFullscreenRotate90 = (el: HTMLDivElement) => {
  const mobileLayout = isMobileLayout();

  el.className = cn("*:tz-1 bg-black fixed fullscreen-rotate-90 h-dvw overflow-hidden z-90 ", {
    "mobile-fullscreen-w": mobileLayout,
    "w-dvh": !mobileLayout,
  });
};

export const resetFakeFullscreenRotate = (el: HTMLDivElement) => {
  el.className = "";
};

export function isFullscreenObs(cb: (isFullscreen: boolean) => void) {
  const fn = () => {
    cb(screenfull.isFullscreen);
  };
  if (screenfull.isEnabled) {
    screenfull.on("change", fn);
  }

  return fn;
}

export function offFullscreenChange(cb: () => void) {
  if (screenfull.isEnabled) {
    screenfull.off("change", cb);
  }
}

export async function exitFullscreen() {
  if (screenfull.isEnabled) {
    await screenfull.exit();
  }

  return screenfull.isEnabled;
}

/* 偵測 mimetype 支援 */
export function getSupportedMimeTypes() {
  const possibleTypes = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=h264,opus",
    "video/mp4;codecs=h264,aac",
  ];

  return possibleTypes.filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
}

export function supportMSEH264Playback() {
  return window.MediaSource && window.MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"');
}

export function resolutionParser(width: number, height: number) {
  let result = "SD";
  if (width >= 1280 && height >= 720) {
    result = "HD";
  }
  if (width >= 1920 && height >= 1080) {
    result = "FHD";
  }
  if (width >= 2000 && height <= 1080) {
    result = "2K";
  }
  if (width >= 3840 && height <= 2160) {
    result = "4K";
  }

  return result;
}

export function protocolSupport() {
  return new Promise<CodecSupport>((resolve) => {
    TXLivePlayer.checkSupport((data) => {
      resolve({ hlsSupported: Hls.isSupported(), webrtcSupported: data.support, h264Supported: data.h264Support });
    });
  });
}

let wakeLock: WakeLockSentinel | null = null;

export async function requestWakeLock() {
  try {
    wakeLock = await navigator.wakeLock.request("screen");
    wakeLock.addEventListener("release", () => console.log("release wake lock"));
  } catch (err) {
    console.warn("No Wake Lock", err);
  }
}

export function releaseWakeLock() {
  wakeLock?.release();
}
